import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FaPaperPlane, FaSmile } from 'react-icons/fa';
import { useChat } from '../../context/ChatContext';
import useTyping from '../../hooks/useTyping';
import EmojiPicker from './EmojiPicker';
import '../../css/Chat.css';

const MessageInput = ({ chatId, disabled = false, onMessageSent }) => {
  const { sendMessage, currentUser } = useChat();
  const { startTyping, stopTyping } = useTyping(chatId);
  
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Scroll a MessageList aljára
  const scrollToBottom = useCallback(() => {
    const messageList = document.querySelector('.chat-message-list');
    if (messageList) {
      const scrollTo = messageList.scrollHeight - messageList.clientHeight;
      messageList.scrollTo({
        top: scrollTo,
        behavior: 'smooth'
      });
    }
  }, []);

  // Auto-focus input-ra
  useEffect(() => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [chatId, disabled]);

  // Cleanup typing timeout
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Üzenet küldése
  const handleSendMessage = useCallback(async () => {
    if (!message.trim() || sending || disabled || !chatId) return;

    const messageText = message.trim();
    setMessage('');
    setSending(true);
    stopTyping();

    try {
      await sendMessage(chatId, messageText);
      // Sikeres küldés után scroll az aljára
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      // Callback hívása is
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (error) {
      console.error('Üzenet küldési hiba:', error);
      // Visszaállítjuk az üzenetet hiba esetén
      setMessage(messageText);
    } finally {
      setSending(false);
      // Focus visszaállítása
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [message, sending, disabled, chatId, sendMessage, stopTyping, onMessageSent, scrollToBottom]);

  // Emoji hozzáadása
  const handleEmojiSelect = useCallback((emoji) => {
    if (inputRef.current) {
      const start = inputRef.current.selectionStart;
      const end = inputRef.current.selectionEnd;
      const newMessage = message.substring(0, start) + emoji + message.substring(end);
      setMessage(newMessage);
      
      // Focus visszaállítása és cursor pozíció
      setTimeout(() => {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    }
  }, [message]);

  // Enter lenyomás kezelése
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  // Szöveg változás kezelése
  const handleMessageChange = useCallback((e) => {
    const newMessage = e.target.value;
    setMessage(newMessage);

    // Typing jelzés kezelése
    if (newMessage.trim() && !disabled && chatId && currentUser) {
      startTyping();
      
      // Typing leállítása 3 másodperc múlva
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        stopTyping();
      }, 3000);
    } else {
      stopTyping();
    }
  }, [startTyping, stopTyping, disabled, chatId, currentUser]);

  // Üzenet blur esetén typing leállítása
  const handleBlur = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    stopTyping();
  }, [stopTyping]);

  const toggleEmojiPicker = useCallback(() => {
    setShowEmojiPicker(prev => !prev);
  }, []);

  if (!chatId) {
    return (
      <div className="message-input-placeholder">
        <span>Válassz egy chatet az üzenetküldéshez</span>
      </div>
    );
  }

  return (
    <div className="chat-message-input-container">
      <div className="chat-message-input-row">
        {/* Üzenet input */}
        <div className="message-input-wrapper">
          <textarea
            ref={inputRef}
            className="chat-message-input"
            placeholder={disabled ? "Chat nem elérhető" : "Írj egy üzenetet..."}
            value={message}
            onChange={handleMessageChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={disabled || sending}
            rows={1}
            style={{
              resize: 'none',
              overflow: 'hidden',
              minHeight: '40px',
              maxHeight: '120px'
            }}
          />
        </div>

        {/* Emoji gomb */}
        <button
          className={`message-action-btn ${showEmojiPicker ? 'active' : ''}`}
          onClick={toggleEmojiPicker}
          disabled={disabled || sending}
          title="Emoji hozzáadása"
        >
          <FaSmile />
        </button>

        {/* Küldés gomb */}
        <button
          className={`chat-message-send-btn ${!message.trim() || sending ? 'disabled' : ''}`}
          onClick={handleSendMessage}
          disabled={!message.trim() || sending || disabled}
          title="Üzenet küldése"
        >
          {sending ? (
            <div className="sending-spinner" />
          ) : (
            <FaPaperPlane />
          )}
        </button>
      </div>

      {/* Küldés státusz */}
      {sending && (
        <div className="message-sending-status">
          <span>Üzenet küldése...</span>
        </div>
      )}

      {/* Emoji Picker */}
      <EmojiPicker
        isOpen={showEmojiPicker}
        onEmojiSelect={handleEmojiSelect}
        onClose={() => {
        
        }}
        position="top"
      />
    </div>
  );
};

export default MessageInput;
